import { MapRegion, Structure, Post, PlanBounds } from '../../types';
import type { ClearViewInsets } from './clearViewCenter';

export interface MapViewProps {
  initialRegion?: MapRegion;
  posts?: Post[];
  // IDs of service areas currently intersecting the viewport. When present,
  // the Map renders only structures whose shard belongs to one of these
  // areas, which keeps an in-memory cache from prior areas (kept around so
  // returning to them is instant) from blowing up the marker-element count
  // and freezing the JS thread on area switches.
  activeAreaIds?: string[];
  // When present, the Map hard-clamps pan and zoom to this bounding box
  // (with an optional minimum zoom so the user can't zoom out past the
  // area). Used to enforce the "one selected area at a time" MVP rule —
  // users must pick a different area through the picker to pan elsewhere.
  lockBounds?: { minLat: number; maxLat: number; minLng: number; maxLng: number };
  lockMinZoom?: number;
  onRegionChange?: (region: MapRegion) => void;
  onStructurePress?: (structure: Structure) => void;
  onPostPress?: (post: Post) => void;
  showUserLocation?: boolean;
}

export interface FocusInClearViewOptions {
  // false forces a jump; omitted lets the map fly a short hop.
  animate?: boolean;
  // Insets the map WILL have once the panel this focus opens has docked.
  // Unset sides fall back to what uiStore has registered right now.
  insets?: Partial<ClearViewInsets>;
}

export interface MapHandle {
  focusOnLocation: (latitude: number, longitude: number, zoom?: number) => void;
  // Web only: bring a structure into the part of the map the docked panels
  // leave clear (flying a short hop, jumping a long one), then play a one-shot
  // arrival ring on it. Callers fall back to focusOnLocation when absent.
  focusInClearView?: (
    latitude: number,
    longitude: number,
    zoom: number,
    opts?: FocusInClearViewOptions
  ) => void;
  // Web only: pan (never zoom) just far enough to bring a point into clear
  // view, and do nothing at all when it is already there.
  ensureInClearView?: (
    latitude: number,
    longitude: number,
    opts?: { insets?: Partial<ClearViewInsets> }
  ) => void;
  // Web only: center on a located post AND open its hover preview card once
  // the pan/zoom settles, so the user can tell which pin they arrived at.
  // Callers fall back to focusOnLocation when absent (native has no hover
  // surface — the centered pin is the affordance there).
  focusOnPost?: (post: Post, zoom?: number) => void;
  // Web only: fit the view to a defense plan's bounds. Callers fall back to
  // focusOnLocation on the centre when absent (native draws no plans yet).
  focusOnBounds?: (bounds: PlanBounds, paddingPx?: number) => void;
}

export const DEFAULT_REGION: MapRegion = {
  // Big Bear Lake, CA
  latitude: 34.2439,
  longitude: -116.9114,
  latitudeDelta: 0.1,
  longitudeDelta: 0.1,
};

// Hard-clamp the pannable area so users can't scroll the map out over
// Europe / Asia, which would load empty tiles and leave structures loaded
// for service areas that aren't visible. Generous enough to cover
// continental North America plus buffer into Central America and Alaska.
export const NORTH_AMERICA_BOUNDS = {
  south: 14, // southern Mexico
  north: 72, // northern Canada
  west: -168, // western Alaska
  east: -52, // Newfoundland
};

// Leaflet zoom level at which North America roughly fills the viewport;
// prevents zooming out past the clamped bounds.
export const MIN_ZOOM = 3;

// One step past OSM's native tile limit (19) — Leaflet stretches the
// last-native tile to fill. Lets users break dense clusters apart at the
// street level instead of stopping at the supercluster maxZoom.
export const MAX_ZOOM = 20;
export const MAX_NATIVE_TILE_ZOOM = 19;

// Pin rendering gate — above this latitude delta (i.e. zoomed out further)
// individual structure pins are NOT rendered and NOT fetched. The map leads
// with observations: structure pins stay hidden until roughly street-level
// zoom (~zoom 16) so every-building dots don't drown out observation
// markers (client ask, June 2026). Also a perf guard — populous areas like
// SF (~225k structures) would otherwise stall the JS thread building tens
// of thousands of marker elements on every viewport change.
export const PIN_RENDER_MAX_DELTA = 0.0055;

// Web (Leaflet) equivalent of PIN_RENDER_MAX_DELTA: log2(360 / 0.0055) ≈ 16.
// Below this zoom the web map renders no structure pins or cluster bubbles —
// the map leads with observations, and properties appear once the user
// zooms to street level. Rendering only is gated; shard data still loads
// at wide zoom so address search keeps working.
export const STRUCTURE_RENDER_MIN_ZOOM_WEB = 16;
